import { useCallback, useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { setPageType } from '@/shared/helpers/page-type';

import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { InsuranceUploadForm } from '../components/upload-form';
import { InsuranceListItem } from '../components/list-item';
import { InsuranceFindTypes } from '../constants';
import { Box, Button, Modal } from '@/shared/ui';
import { CustomersInsuranceResponse, ICustomersInsuranceUploadResponse } from '@/shared/endpoints/customers/models/response';
import { CustomersController } from '@/shared/endpoints/customers';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';

import InsuranceListDoctors from '@/assets/images/insurance-list-doctors.svg';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';
import { useSearchParams } from 'react-router-dom';
import { filterInsurances } from '@/shared/helpers/insurance';
import AddInsuranceManual from './add-manual-split-layout-support';

export interface IInsuranceListProps {
    findType?: InsuranceFindTypes;
    onContinue(): void;
    onGoBack(): void;
}

const InsuranceList = ({ findType, onContinue, onGoBack }: IInsuranceListProps) => {
    const theme = useTheme();
    const breakWord = useMediaQuery(theme.breakpoints.down(1351));
    const isDownXL = useMediaQuery(theme.breakpoints.down('xl'));

    const [, setSearchParams] = useSearchParams();

    const dispatch = useAppDispatch();
    const user = useAppSelector(selectOnboardingUser)!;
    const [deletingInsurance, setDeletingInsurance] = useState<CustomersInsuranceResponse | null>(null);
    const [addFormType, setAddFormType] = useState<InsuranceFindTypes | null | undefined>(null);
    const filteredInsurances = useMemo(() => filterInsurances(user.insurances), [user.insurances]);

    const allInsurances = useMemo(() => [...filteredInsurances, ...user.insuranceUploads], [filteredInsurances, user.insuranceUploads]);

    const onDeleteConfirm = async () => {
        if (!deletingInsurance) return;
        const makeDelete = (deletingInsurance as ICustomersInsuranceUploadResponse).front
            ? CustomersController.insuranceUploadDelete
            : CustomersController.insuranceDelete;

        await dispatch(
            makeDelete({
                id: deletingInsurance.id,
                version: deletingInsurance.ver
            })
        );
        setDeletingInsurance(null);
    };

    const handleToggleModal = useCallback(
        (type?: InsuranceFindTypes | null) => {
            setAddFormType(type);
            if (type === 'MANUAL') {
                setSearchParams('form=MANUAL');
            } else if (type === 'UPLOAD') {
                setSearchParams('form=UPLOAD');
            } else {
                setSearchParams('');
            }
        },
        [setSearchParams]
    );

    const _conv_page_type = 'insurance_list';

    const handleAddInsurance = useCallback(
        async (insurance: CustomersInsuranceResponse | null) => {
            handleToggleModal(null);

            if (!insurance || !user) return null;
        },
        [handleToggleModal, user]
    );

    useEffect(() => {
        setPageType(_conv_page_type);
    }, []);

    return (
        <SplitLayout
            sideContent={
                <div className="xl-max:flex xl-max:flex-col xl-max:items-center">
                    {isDownXL && <img width={140} src={InsuranceListDoctors} alt="doctors" />}

                    <h2
                        className="insurance__heading mb-[2.25rem] font-agrandir text-[3.125rem] sm-max:text-[1.5625rem] xl-max:text-center"
                        data-testid="insurance-list-page-title"
                    >
                        {findType === 'LOOKUP' ? (
                            <>
                                This is what we found! {!isDownXL && <br />} <span className="text-primary-700">Please Review</span> before
                                you proceed.
                            </>
                        ) : (
                            <>
                                Your Insurance {!isDownXL && breakWord && <br />} has been {!breakWord && <br />} added.{' '}
                                {breakWord && <br />} <span className="text-primary-700">Please Review</span> <br />
                                before you proceed.
                            </>
                        )}
                    </h2>

                    {!isDownXL && (
                        <Box sx={{ width: 438, position: 'relative', maxWidth: '100%', borderTopRightRadius: 0, p: '25px' }}>
                            <div className="text-2xl">
                                Here’s peace of mind! Our <span className="font-atkinsonBold italic">MedBox Pharmacists</span> will
                                coordinate with your prescribers to organize & sort your medications.
                            </div>
                            <img
                                className="absolute bottom-0 right-0 translate-x-[60%] translate-y-[65%]"
                                width={140}
                                src={InsuranceListDoctors}
                                alt="doctors"
                            />
                        </Box>
                    )}
                </div>
            }
            mainContent={
                <Box
                    bgWhite
                    shadow
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        [theme.breakpoints.up('lg')]: { padding: '1.85rem 2.8rem' },
                        [theme.breakpoints.down('xl')]: { padding: '1.25rem' }
                    }}
                >
                    <div className="w-full">
                        <h4 className="font-agrandir text-[1.75rem]">{user?.userProfile?.firstName}’s Insurance</h4>
                        {allInsurances.map(insurance => (
                            <InsuranceListItem
                                className="mt-4 w-full"
                                hasDelete
                                key={insurance.id}
                                insurance={insurance}
                                onDelete={setDeletingInsurance}
                            />
                        ))}
                        <div className="flex flex-col items-center w-full pt-6.5 gap-[.9375rem] sm:gap-[1.25rem]">
                            <Button
                                className="insurance__continue_btn"
                                type="submit"
                                sx={{
                                    width: '100%',
                                }}
                                onClick={onContinue}
                                showEndIcon
                                testId="button-insurance-list-continue"
                                fontClassName='font-bold'
                            >
                                <span>Save & Continue</span>
                            </Button>
                            <Button
                                className="insurance__add_insurance_btn"
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                }}
                                onClick={() => {
                                    handleToggleModal('UPLOAD');
                                }}
                                testId="button-insurance-list-add-additional"
                                fontClassName='font-bold'
                            >
                                <span className="font-dmSansMedium font-bold">Add Additional Insurance</span>
                            </Button>
                            <Button
                                fontClassName="font-dmSansMedium font-bold text-17 leading-[130%] tracking-[.0213rem]"
                                testId="button-insurance-list-back"
                                variant="text"
                                className="button-insurance-list-back w-fit"
                                onClick={onGoBack}
                            >
                                {`< Back`}
                            </Button>
                        </div>
                    </div>
                </Box>
            }
            extraContent={
                <>
                    {isDownXL && (
                        <Box sx={{ marginTop: '2.5rem', width: 438, position: 'relative', maxWidth: '100%', borderBottomLeftRadius: 0 }}>
                            <div className="text-2xl sm-max:text-lg">
                                Here’s peace of mind! Our <span className="font-bold italic">MedBox Pharmacists</span> will coordinate with
                                your prescribers to organize & sort your medications.
                            </div>
                        </Box>
                    )}
                    <Modal
                        open={addFormType === 'MANUAL'}
                        onClose={() => handleToggleModal(null)}
                        contentClassName="!w-fit !max-w-[1100px] !min-w-0 px-[20px] pt-[42px] pb-[30px] sm:px-[45px] py-[40px] bg-white xl:!bg-mint-100"
                        dataTestId="insurance-list-manual-add"
                    >
                        <AddInsuranceManual
                            onBack={handleToggleModal}
                            onSuccess={handleAddInsurance}
                        />
                    </Modal>
                    <Modal
                        open={addFormType === 'UPLOAD'}
                        onClose={() => handleToggleModal(null)}
                        contentClassName="sm-max:w-[400px] sm-max:!max-w-[98%]"
                        dataTestId="insurance-list-upload-add"
                    >
                        <div
                            className="flex max-w-[100%] flex-col items-center justify-center"
                            data-testid="insurance-list-upload-add-container">
                            <h3
                                className="mb-[2.1875rem] text-center font-agrandir text-[1.875rem] sm-max:text-[1.5625rem]"
                                data-testid="insurance-list-upload-add-title"
                            >
                                Please Upload a Photo of Your Insurance Card
                            </h3>

                            <InsuranceUploadForm
                                hasInsurance={!!allInsurances.length}
                                onSuccess={handleAddInsurance}
                                onBack={handleToggleModal}
                            />
                        </div>
                    </Modal>
                    <ConfirmModal
                        open={!!deletingInsurance}
                        onCancel={() => setDeletingInsurance(null)}
                        onConfirm={() => onDeleteConfirm()}
                        title="Are you sure you want to remove this insurance?"
                        confirmText="Yes, Remove"
                        cancelText="Cancel Removal"
                    >
                        {deletingInsurance && <InsuranceListItem insurance={deletingInsurance} />}
                    </ConfirmModal>
                </>
            }
        />
    );
};

export default InsuranceList;

// export default withScrollRestoration(InsuranceList);
